import React, { useEffect, useRef, useState } from 'react';
import { IMAGE_UPLOAD_MAX_MB } from '../constants/uploadLimits';
import { ImageCropModal } from './ui/ImageCropModal';
import { EntityLogo } from './ui/EntityLogo';
import type { CropPresetId } from '../constants/imageCropPresets';
import { prepareImageFileForUpload } from '../utils/prepareImageFileForUpload';

type EntityPhotoUploadProps = {
  label: string;
  helpText?: string;
  currentUrl?: string | null;
  /** Circle for club/user-style avatars; banner for event flyers. */
  shape?: 'circle' | 'banner';
  cropPreset?: CropPresetId;
  maxSizeMb?: number;
  disabled?: boolean;
  onUpload: (file: File) => Promise<string>;
  onDelete?: () => Promise<void>;
  onUpdated?: (url: string | null) => void;
  onError?: (message: string) => void;
};

/**
 * Single-image upload for clubs (profile) and events (flyer).
 * Opens aspect-accurate crop before upload.
 */
export const EntityPhotoUpload: React.FC<EntityPhotoUploadProps> = ({
  label,
  helpText,
  currentUrl = null,
  shape = 'circle',
  cropPreset,
  maxSizeMb = IMAGE_UPLOAD_MAX_MB,
  disabled = false,
  onUpload,
  onDelete,
  onUpdated,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(currentUrl);
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  const preset: CropPresetId =
    cropPreset ?? (shape === 'banner' ? 'event_flyer' : 'entity_logo');

  useEffect(() => {
    setPreview(currentUrl);
  }, [currentUrl]);

  const reportError = (msg: string) => {
    setLocalError(msg);
    onError?.(msg);
  };

  const validate = (file: File): boolean => {
    const typeOk =
      file.type.startsWith('image/') ||
      !file.type ||
      /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
    if (!typeOk) {
      reportError('Please upload a JPEG, PNG, or WebP image');
      return false;
    }
    return true;
  };

  const uploadCropped = async (file: File) => {
    setBusy(true);
    setLocalError(null);
    try {
      const path = await onUpload(file);
      setPreview(path);
      onUpdated?.(path);
    } catch (e: unknown) {
      setPreview(currentUrl);
      reportError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleCropConfirm = async ({ file }: { file: File }) => {
    setCropOpen(false);
    setCropFile(null);
    await uploadCropped(file);
  };

  const handleFile = (file: File | undefined) => {
    if (!file || disabled || busy || preparing) return;
    if (!validate(file)) return;
    void (async () => {
      setPreparing(true);
      setLocalError(null);
      try {
        const prepared = await prepareImageFileForUpload(file, {
          maxLongEdge: 2048,
          maxBytes: maxSizeMb * 1024 * 1024,
        });
        setCropFile(prepared);
        setCropOpen(true);
      } catch {
        reportError("We couldn't prepare this photo. Try another image.");
      } finally {
        setPreparing(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    })();
  };

  const handleDelete = async () => {
    if (!onDelete || disabled || busy || preparing) return;
    setBusy(true);
    setLocalError(null);
    try {
      await onDelete();
      setPreview(null);
      onUpdated?.(null);
    } catch (e: unknown) {
      reportError(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  const frameClass =
    shape === 'banner'
      ? 'w-full max-w-md aspect-[16/10]'
      : 'w-24 h-24';

  const working = busy || preparing;

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-medium text-white">{label}</h3>
        {helpText ? <p className="text-meta mt-1">{helpText}</p> : null}
      </div>

      <div className={`flex ${shape === 'banner' ? 'flex-col' : 'flex-row items-center'} gap-4`}>
        {preview ? (
          shape === 'banner' ? (
            <div className={`${frameClass} shrink-0`}>
              <EntityLogo
                src={preview}
                alt=""
                rounded="lg"
                className="w-full h-full"
              />
            </div>
          ) : (
            <EntityLogo
              src={preview}
              alt=""
              rounded="full"
              className={`${frameClass} shrink-0`}
            />
          )
        ) : (
          <div
            className={`${frameClass} overflow-hidden border border-white/[0.08] bg-[#0a0a0a] shrink-0 flex items-center justify-center text-meta text-xs rounded-xl`}
          >
            No photo
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled || working}
            className="btn-secondary btn-sm cursor-pointer"
            onClick={() => inputRef.current?.click()}
          >
            {preparing
              ? 'Preparing image…'
              : busy
                ? 'Working…'
                : preview
                  ? 'Change photo'
                  : 'Upload photo'}
          </button>
          {preview && onDelete ? (
            <button
              type="button"
              disabled={disabled || working}
              className="btn-ghost btn-sm cursor-pointer"
              onClick={handleDelete}
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/*"
        className="hidden"
        disabled={disabled || working}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {localError ? (
        <p className="text-sm text-red-300" role="alert">
          {localError}
        </p>
      ) : null}

      <ImageCropModal
        isOpen={cropOpen}
        onClose={() => {
          setCropOpen(false);
          setCropFile(null);
        }}
        file={cropFile}
        presetId={preset}
        onConfirm={handleCropConfirm}
        busy={busy}
      />
    </div>
  );
};

export default EntityPhotoUpload;
